import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { fetchWithAuth } from '@/lib/api'
import { isAbortFetchError } from '@/lib/api/core'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/providers/auth-provider'
import {
  clearUserHiddenWorkspace,
  clearUserHiddenWorkspaces,
  markWorkspacesUserHidden,
} from '@/lib/user-hidden-workspaces'

export interface Workspace {
  id: string
  name: string
  slug: string
  type: 'personal' | 'work' | 'client' | 'community' | 'other'
  icon: string | null
  color: string | null
  contentRoot: string | null
  showInSidebar: boolean
}

interface WorkspaceContextValue {
  workspaces: Workspace[]
  activeWorkspaceIds: string[]
  toggleWorkspace: (id: string) => void
  /** Show only this workspace; hide all others from browse/open-tabs scope. */
  hideOtherWorkspaces: (id: string) => void
  setActiveWorkspaceIds: (ids: string[]) => void
  isWorkspaceActive: (id: string) => boolean
  activeWorkspaces: Workspace[]
  activeFolderPaths: string[]
  refetchWorkspaces: () => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

/** Dev-only: Vite HMR can remount ResizableLayout before WorkspaceProvider; avoid white-screen. */
let devContextFallback: WorkspaceContextValue | null = null

const STORAGE_KEY = 'flownotes_active_workspaces'
const LEGACY_WORKING_SCOPE_STORAGE_KEY = 'flownotes_working_scope'

type ApiOrg = {
  id: string
  name: string
  slug: string
  type: string
  icon: string | null
  color: string | null
  content_root: string | null
  show_in_sidebar: boolean
}

function mapOrgToWorkspace(o: ApiOrg): Workspace {
  return {
    id: o.id,
    name: o.name,
    slug: o.slug,
    type: (o.type as Workspace['type']) || 'work',
    icon: o.icon ?? null,
    color: o.color ?? null,
    contentRoot: o.content_root ?? null,
    showInSidebar: o.show_in_sidebar !== false,
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { session, isLoading: authLoading, isAuthConfigured } = useAuth()
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [activeWorkspaceIds, setActiveWorkspaceIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })
  const prevAuthUserIdRef = useRef<string | null>(null)
  const activeIdsRef = useRef(activeWorkspaceIds)
  activeIdsRef.current = activeWorkspaceIds

  const setActiveWorkspaceIdsPublic = useCallback((ids: string[]) => {
    // Activating a workspace is explicit (undo Only, Enable, palette).
    clearUserHiddenWorkspaces(ids)
    setActiveWorkspaceIds(ids)
  }, [])

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(activeWorkspaceIds))
  }, [activeWorkspaceIds])

  // Different Supabase user on same browser: drop stale workspace ids from prior session.
  useEffect(() => {
    const uid = session?.user?.id ?? null
    if (prevAuthUserIdRef.current && uid && prevAuthUserIdRef.current !== uid) {
      setActiveWorkspaceIds([])
    }
    prevAuthUserIdRef.current = uid
  }, [session?.user?.id])

  const sanitizeActiveWorkspaceIds = useCallback((memberWorkspaces: Workspace[], prev: string[]) => {
    const memberIds = new Set(memberWorkspaces.map((w) => w.id))
    const sanitized = prev.filter((id) => memberIds.has(id))
    if (sanitized.length > 0) return sanitized
    return memberWorkspaces.length > 0 ? memberWorkspaces.map((w) => w.id) : []
  }, [])

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_WORKING_SCOPE_STORAGE_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  const refetchWorkspaces = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/organizations`)
      if (!response.ok) return
      const data: ApiOrg[] = await response.json()
      const mapped = (data || []).map(mapOrgToWorkspace)
      setWorkspaces(mapped)
      setActiveWorkspaceIds((prev) => sanitizeActiveWorkspaceIds(mapped, prev))
    } catch (err) {
      if (isAbortFetchError(err)) return
      console.error('Failed to fetch workspaces:', err)
    }
  }, [sanitizeActiveWorkspaceIds])

  const hideOtherWorkspaces = useCallback((id: string) => {
    const hidden = activeIdsRef.current.filter((wid) => wid !== id)
    markWorkspacesUserHidden(hidden)
    clearUserHiddenWorkspace(id)
    setActiveWorkspaceIds([id])
  }, [])

  useEffect(() => {
    if (isAuthConfigured && authLoading) return
    if (isAuthConfigured && !session) return
    let cancelled = false
    refetchWorkspaces().then(() => {
      if (cancelled) return
    })
    return () => {
      cancelled = true
    }
  }, [refetchWorkspaces, isAuthConfigured, authLoading, session])

  // Honor ?workspace= after invite accept or deep links (one-shot, then strip param).
  useEffect(() => {
    if (workspaces.length === 0) return
    const params = new URLSearchParams(window.location.search)
    const workspaceId = params.get('workspace')
    if (!workspaceId) return
    if (!workspaces.some((w) => w.id === workspaceId)) return
    hideOtherWorkspaces(workspaceId)
    params.delete('workspace')
    const nextSearch = params.toString()
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`
    window.history.replaceState({}, '', nextUrl)
  }, [workspaces, hideOtherWorkspaces])

  // After sign-in / first session hydrate, refetch orgs (mount effect often ran with no JWT).
  useEffect(() => {
    if (!supabase) return
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        session?.access_token &&
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')
      ) {
        void refetchWorkspaces()
      }
    })
    return () => subscription.unsubscribe()
  }, [refetchWorkspaces])

  const toggleWorkspace = (id: string) => {
    setActiveWorkspaceIds((prev) => {
      if (prev.includes(id)) {
        markWorkspacesUserHidden([id])
        return prev.filter((wid) => wid !== id)
      }
      clearUserHiddenWorkspace(id)
      return [...prev, id]
    })
  }

  const isWorkspaceActive = (id: string) => activeWorkspaceIds.includes(id)

  const activeWorkspaces = workspaces.filter((w) =>
    activeWorkspaceIds.includes(w.id)
  )

  const activeFolderPaths = activeWorkspaces
    .map((w) => w.contentRoot)
    .filter((p): p is string => p !== null)

  const contextValue: WorkspaceContextValue = {
    workspaces,
    activeWorkspaceIds,
    toggleWorkspace,
    hideOtherWorkspaces,
    setActiveWorkspaceIds: setActiveWorkspaceIdsPublic,
    isWorkspaceActive,
    activeWorkspaces,
    activeFolderPaths,
    refetchWorkspaces,
  }

  if (import.meta.env.DEV) {
    devContextFallback = contextValue
  }

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) {
    if (import.meta.env.DEV && devContextFallback) {
      return devContextFallback
    }
    throw new Error('useWorkspace must be used within WorkspaceProvider')
  }
  return ctx
}
